import { Component, inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { interval } from "rxjs";
import { HeaderDialogComponent } from "../../modules/dialog/header-dialog/header-dialog.component";
import { BigButtonComponent } from "../big-button/big-button.component";
import { parseNumber } from "../../utils/number-utils.service";
import { TranslatePipe } from "../../pipes/translate.pipe";

@Component({
  selector: "vl-confirmation-dialog",
  standalone: true,
  imports: [HeaderDialogComponent, BigButtonComponent, TranslatePipe],
  templateUrl: "./confirmation-dialog.component.html",
  styleUrls: ["./confirmation-dialog.component.scss"],
})
export class ConfirmationDialogComponent {
  data = inject(MAT_DIALOG_DATA);
  dialog = inject(MatDialog);
  dialogRef = inject(MatDialogRef<ConfirmationDialogComponent>);

  // --- Auto-validate config ---
  autoDelayMs = Math.max(
    0,
    parseNumber(
      this.data?.autoDelayMs ??
        (this.data?.autoDelaySec != null ? this.data.autoDelaySec * 1000 : 0),
    ),
  );
  autoAction: "confirm" | "cancel" =
    this.data?.autoAction === "cancel" ? "cancel" : "confirm";
  tickMs = Math.max(50, parseNumber(this.data?.progressTickMs ?? 100)); // rafraîchissement barre/chrono

  // --- Runtime state ---
  countdownSec = Math.ceil(this.autoDelayMs / 1000) || 0;
  progressPct = 0;
  private _autoStartedAt = 0;
  private _autoFired = false;

  constructor() {
    // Comportement existant (disableClose + backdropClick remappé) conservé
    if (this.data?.values) {
      this.dialogRef.disableClose = true;
      this.dialogRef
        .backdropClick()
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          this.dialogRef.close(this.data.values.backdropClick);
        });
    }

    // Compte à rebours + barre de progression (optionnel)
    if (this.autoDelayMs > 0) {
      this._autoStartedAt = Date.now();
      interval(this.tickMs)
        .pipe(takeUntilDestroyed())
        .subscribe(() => this._onTick());
    }
  }

  ngOnInit(): void {}

  private _onTick() {
    if (this._autoFired) return;

    const elapsed = Date.now() - this._autoStartedAt;
    const remaining = Math.max(0, this.autoDelayMs - elapsed);

    this.countdownSec = Math.ceil(remaining / 1000);
    this.progressPct =
      this.autoDelayMs > 0
        ? Math.min(100, (elapsed / this.autoDelayMs) * 100)
        : 0;

    if (remaining <= 0) {
      this._autoFired = true;
      if (this.autoAction === "cancel") {
        this.onCancel();
      } else {
        this.onConfirm();
      }
    }
  }

  onCancel() {
    this._autoFired = true;
    if (this.data?.values) {
      this.dialogRef.close(this.data.values.cancel);
    } else {
      this.dialogRef.close(false);
    }
  }

  onConfirm() {
    this._autoFired = true;
    if (this.data?.values) {
      this.dialogRef.close(this.data.values.confirm);
    } else {
      this.dialogRef.close(true);
    }
  }
}
